import { useEffect, useMemo, useRef, useState } from 'react';
import { makeMemoryRound } from './memory';
import { elapsed, type DrillProps } from './meta';

const GAP_MS = 250;
const READY_MS = 1200;

export function MemoryDrill({ level, seed, onDone }: DrillProps) {
  const round = useMemo(() => makeMemoryRound(level, seed), [level, seed]);
  const started = useRef(performance.now());
  // step: -1 = 준비, 짝수 2k = k번째 카드 보이기, 홀수 = 카드 사이 빈 화면, 2n 이상 = 질문
  const [step, setStep] = useState(-1);
  const [qi, setQi] = useState(0);
  const [hits, setHits] = useState(0);
  const [picked, setPicked] = useState<string | null>(null);
  const watching = step < round.events.length * 2;

  useEffect(() => {
    if (!watching) return undefined;
    const wait = step < 0 ? READY_MS : step % 2 === 0 ? round.showMs : GAP_MS;
    const id = window.setTimeout(() => setStep((s) => s + 1), wait);
    return () => window.clearTimeout(id);
  }, [step, watching, round.showMs]);

  if (watching) {
    const e = step >= 0 && step % 2 === 0 ? round.events[step / 2] : null;
    return (
      <div className="drill mem-drill">
        <div className="drill-progress">{step < 0 ? '준비…' : `${Math.floor(step / 2) + 1} / ${round.events.length}`}</div>
        <div className="mem-stage" aria-live="polite">
          {step < 0 && <p className="muted">{round.players.join(' · ')} — 카드를 잘 보세요</p>}
          {e && (
            <>
              <div className="mem-player">{e.player}</div>
              <div className={`mem-card ${round.colored ? e.color : 'plain'}`}>{e.num}</div>
            </>
          )}
        </div>
      </div>
    );
  }

  const q = round.questions[qi];
  const choose = (c: string) => {
    if (picked !== null) return;
    setPicked(c);
    const ok = c === q.answer;
    if (ok) setHits((h) => h + 1);
    window.setTimeout(() => {
      if (qi + 1 >= round.questions.length) {
        onDone({
          correct: hits + (ok ? 1 : 0),
          total: round.questions.length,
          duration_ms: elapsed(started.current),
          detail: { seed, events: round.events.length, players: round.players.length, show_ms: round.showMs },
        });
        return;
      }
      setPicked(null);
      setQi(qi + 1);
    }, ok ? 500 : 1400);
  };

  return (
    <div className="drill mem-drill">
      <div className="drill-progress">질문 {qi + 1} / {round.questions.length}</div>
      <h2 className="calc-q">{q.text}</h2>
      <div className="choice-list">
        {q.choices.map((c) => (
          <button
            key={c} type="button" disabled={picked !== null} onClick={() => choose(c)}
            className={`choice ${picked !== null && c === q.answer ? 'right' : ''} ${picked === c && c !== q.answer ? 'wrong' : ''}`}
          >
            {c}
          </button>
        ))}
      </div>
      <p className="rule-feedback" aria-live="polite">{picked === null ? ' ' : picked === q.answer ? '정답!' : `정답은 ${q.answer}`}</p>
    </div>
  );
}
